import type { ReactNode } from 'react';
import { motion } from 'motion/react';
import { cva } from 'class-variance-authority';
import clsx from 'clsx';
import {Flex, IconButton, Text, iconWeight, Icon} from '@nula/core-ui';
import styles from './side-panel.module.css';

interface SidePanelProps {
  title: string;
  /** Material icon name shown in a circle next to the title */
  icon?: string;
  onClose: () => void;
  titleExtra?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Extra class merged onto the body — use to override padding for dense panels */
  bodyClassName?: string;
  /** Marks the panel as belonging to the assistant. The floating overlay's
   * click-outside handler uses this to tell its own portalled panels from a
   * genuine dismissal. */
  'data-assistant-surface'?: boolean;
}

const sidePanel = cva(styles.base);
const header = cva(styles.header);
const iconCircle = cva(styles.iconCircle);
const body = cva(styles.body);
const footer = cva(styles.footer);

/**
 * Shared inline side-panel wrapper for the right sidebar overlay.
 * Used by HistoryPanel, CommentSidebar, and any future right-panel views.
 */
export function SidePanel({ title, icon, onClose, titleExtra, children, footer: footerContent, bodyClassName, 'data-assistant-surface': assistantSurface }: SidePanelProps) {
  return (
    <Flex
      vertical
      component="aside"
      className={sidePanel()}
      data-assistant-surface={assistantSurface ? '' : undefined}
    >
      <Flex
        align="center"
        justify="space-between"
        className={header()}
      >
        <Flex align="center" gap="md">
          {icon && (
            <Flex
              align="center"
              justify="center"
              className={iconCircle()}
            >
              <Icon name={icon} size={18} weight={iconWeight.light} />
            </Flex>
          )}
          <Text className={styles.title}>
            {title}
          </Text>
          {titleExtra}
        </Flex>
        <IconButton size="sm" onClick={onClose} aria-label={`Close ${title.toLowerCase()}`}
          className={styles.closeButtonFlex}
        >
          <motion.div
            whileHover={{ rotate: 90 }}
            transition={{ duration: 0.2 }}
            className={styles.iconRotateContainer}
          >
            <Icon name="close" size={16} weight={iconWeight.light} />
          </motion.div>
        </IconButton>
      </Flex>

      <Flex vertical className={clsx(body(), bodyClassName)}>
        {children}
      </Flex>

      {footerContent && (
        <Flex
          vertical
          className={footer()}
        >
          {footerContent}
        </Flex>
      )}
    </Flex>
  );
}
